import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { MAX_ITEMS_PER_PAGE } from 'modules/shift_management/hooks/reducers/pagination'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'
import type ShiftsRow from 'modules/shift_management/store/internal/models/ShiftsRow'

import useGetShiftsRows from './useGetShiftRows'

const useLoadBootstrapData = () => {
  const [{ view }] = useSchedule()
  const [, dispatchPagination] = usePagination()
  const [, dispatchShiftsRows] = useShiftsRows()
  const [, dispatchUI] = useUI()

  const loadBootstrapData = (shiftsRows: ShiftsRow[]) => {
    dispatchPagination({ type: 'CHECK_LAST_PAGE', shiftsRows, view })
    dispatchShiftsRows({ type: 'BOOTSTRAP', shiftsRows })
    dispatchUI({ type: 'NOT_LOADING' })
  }

  return { loadBootstrapData }
}

const useLoadPage = () => {
  const [{ view }, dispatchSchedule] = useSchedule()
  const [, dispatchPagination] = usePagination()
  const [, dispatchShiftsRows] = useShiftsRows()

  const loadPage = async (shiftsRows: ShiftsRow[]) => {
    dispatchSchedule({ type: 'ADD_SHIFTS_ROWS', shiftsRows })
    dispatchPagination({ type: 'CHECK_LAST_PAGE', shiftsRows, view })
    dispatchShiftsRows({ type: 'ADD_SHIFTS_ROWS', shiftsRows })
  }

  return { loadPage }
}

const useFetchShiftsRows = () => {
  const [filters] = useFilters()
  const [features] = useEnabledFeatures()
  const [{ page }] = usePagination()
  const [{ view }] = useSchedule()
  const { shiftsRows, loading } = useGetShiftsRows()

  const { loadBootstrapData } = useLoadBootstrapData()
  const { loadPage } = useLoadPage()

  useUnsafeEffect(() => {
    if (loading) return

    if (page === 1) {
      loadBootstrapData(shiftsRows)
    } else {
      loadPage(shiftsRows)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, view, filters, features, loading, shiftsRows])

  useUnsafeEffect(() => {
    const { length } = shiftsRows
    if (length <= MAX_ITEMS_PER_PAGE || !view.isByEmployees) return

    track(trackingEvents.shiftManagement.requestShiftManagementEmployees, {
      view: view.viewType,
      view_type_by: view.groupType,
      // As this event is triggered only if the page is not 1, we are safe,
      // but we should consider to change it if we enable pagination for the new areas view, role view, etc
      number_of_employees_in_view: shiftsRows.length,
    })
  }, [shiftsRows, view])

  return { shiftsRows, loading }
}

export default useFetchShiftsRows
